/**
 * iQulture score-result contract.
 *
 * PROVISIONAL SCORES (Option B, 3 Oct 2026). A foundation nobody could measure
 * used to be dropped and its weight re-distributed over the rest — so a
 * MISSING Motivation scored like a good one (prod: 79.3 with M unknown, 59.5
 * with M = 0). Now a missing foundation (or the missing part of one) counts as
 * 0 in the headline: `score` is the LOWER bound of what the citizen would
 * score with the evidence complete, `completeness.range.max` the upper bound,
 * and the result is `provisional` while any evidence is missing. Removing a
 * foundation can therefore never raise the score.
 *
 * The canonical, cross-cutting shape both the Brain (real LLM path AND the
 * deterministic MESH stub) and the oracle MUST agree on for a completed
 * CheckUp calculation. Brain validates its sub-responses (M/E/S/H) piecemeal,
 * but the ASSEMBLED score result had no single contract — so a malformed
 * assembly (out-of-range score, bad badge, wrong foundation keys) could slip
 * through to persistence. `validateIQultureScoreResult` is the guard.
 *
 * Mirrors `IQultureScoreCalculation` in
 * apps/brain-service/.../learning/iqulture/iqulture.config.ts.
 */
import { z } from 'zod';
import type { IQultureCompleteness, IQultureFoundation } from '@tonnie/types';

/** Badge band — strict `>` boundaries: ≤50 noob, ≤80 potential, >80 pro. */
export const iqultureBadgeSchema = z.enum(['noob', 'potential', 'pro']);
export type IQultureBadgeId = z.infer<typeof iqultureBadgeSchema>;

const score0to100 = z.number().min(0).max(100);
const fraction = z.number().min(0).max(1);

/** The four MESH foundations, in their canonical order. */
export const IQULTURE_FOUNDATION_KEYS = [
  'M',
  'E',
  'S',
  'H',
] as const satisfies readonly IQultureFoundation[];
export type IQultureFoundationKey = IQultureFoundation;

/**
 * The foundations' weights in the iQulture score: 0.25·M + 0.25·E + 0.35·S +
 * 0.15·H. Fixed — never re-distributed over the foundations present.
 */
export const IQULTURE_FOUNDATION_WEIGHTS = {
  M: 0.25,
  E: 0.25,
  S: 0.35,
  H: 0.15,
} as const satisfies Record<IQultureFoundationKey, number>;

/** MESH foundations — each 0–100 or null (null = no data for that foundation). */
export const iqultureFoundationsSchema = z.object({
  M: score0to100.nullable(),
  E: score0to100.nullable(),
  S: score0to100.nullable(),
  H: score0to100.nullable(),
});

/** The weights the score used — always the fixed {@link IQULTURE_FOUNDATION_WEIGHTS}. */
export const iqultureWeightsSchema = z
  .object({ M: fraction, E: fraction, S: fraction, H: fraction })
  .refine(
    (w) =>
      IQULTURE_FOUNDATION_KEYS.every((k) => Math.abs(w[k] - IQULTURE_FOUNDATION_WEIGHTS[k]) < 1e-9),
    { message: 'validation.iqulture.weightsFixed' }
  );

/**
 * How much of the evidence the score stands on.
 *
 *   • `foundationCoverage` — per foundation, the share of its evidence present:
 *     0 for a foundation not measured, 1 when complete, between for E without
 *     its native/target-language part.
 *   • `coverage` — Σ weight·coverage over the foundations (1 = complete).
 *   • `range` — the score with the missing evidence at its worst (`min`, the
 *     headline) and at its best (`max`).
 *   • `unlockGains` — the points completing each foundation's evidence could
 *     add, at most (0 when complete). Computed by the engine so no client does
 *     arithmetic on a score.
 */
export const iqultureCompletenessSchema = z.object({
  coverage: fraction,
  foundationCoverage: z.object({ M: fraction, E: fraction, S: fraction, H: fraction }),
  range: z.object({ min: score0to100, max: score0to100 }),
  unlockGains: z.object({
    M: score0to100,
    E: score0to100,
    S: score0to100,
    H: score0to100,
  }),
}) satisfies z.ZodType<IQultureCompleteness>;

/** The assembled iQulture score result. */
export const iqultureScoreResultSchema = z.object({
  /** The headline — the lower bound while `provisional`. */
  score: score0to100,
  badge: iqultureBadgeSchema,
  foundations: iqultureFoundationsSchema,
  weights: iqultureWeightsSchema,
  flags: z.array(z.string()),
  /** Some evidence is missing: the score is the lower bound of `completeness.range`. */
  provisional: z.boolean(),
  completeness: iqultureCompletenessSchema,
});

export type IQultureScoreResult = z.infer<typeof iqultureScoreResultSchema>;

/**
 * The badge bands' EXCLUSIVE lower bounds: a score above `potential` is
 * POTENTIAL, above `pro` is PRO, anything else NOOB. The one place a score
 * becomes a badge — {@link badgeForScore} reads it, and so does the business
 * API's SQL form of the same rule (`business/iqulture-badge.ts`), so a Pool
 * filter can never test a different band than the card shows. Front ends
 * never read it: they render the badge the API sends (root CLAUDE.md rule 9).
 */
export const IQULTURE_BADGE_LOWER_BOUNDS = { potential: 50, pro: 80 } as const;

/**
 * Assert the band agrees with the score (defends against a badge/score
 * mismatch — e.g. a 90 tagged `noob`). Strict `>` boundaries, matching
 * `scoreToBadge` + the oracle's `goldenBadgeForScore`.
 */
export function badgeForScore(score: number): IQultureBadgeId {
  if (score > IQULTURE_BADGE_LOWER_BOUNDS.pro) return 'pro';
  if (score > IQULTURE_BADGE_LOWER_BOUNDS.potential) return 'potential';
  return 'noob';
}

/**
 * The badge an API sends beside a score: the badge STORED with the assessment
 * when it is a badge id, otherwise {@link badgeForScore}. `null` only when there
 * is no score.
 *
 * The stored value is not always an id — rows the Brain wrote before the
 * lowercase standardisation (migration 0066) and the `reasoning_results`
 * fallback carry a display LABEL (`POTENTIAL`, or a localised `PRINCIPIANTE`).
 * Server-side only: clients draw the badge they are sent (root CLAUDE.md rule
 * 9). business-web used to derive its own, with thresholds that were not these,
 * and a 55 read POTENTIAL in the drawer and NOOB on the Vetting card.
 */
export function resolveIqultureBadge(
  stored: string | null | undefined,
  score: number
): IQultureBadgeId;
export function resolveIqultureBadge(
  stored: string | null | undefined,
  score: number | null | undefined
): IQultureBadgeId | null;
export function resolveIqultureBadge(
  stored: string | null | undefined,
  score: number | null | undefined
): IQultureBadgeId | null {
  const parsed = iqultureBadgeSchema.safeParse(stored?.toLowerCase());
  if (parsed.success) return parsed.data;
  return score == null ? null : badgeForScore(score);
}

/**
 * The CheckUp result as the Brain sends it to platform-api (`POST
 * /reasoning/iqulture/calculate/:talentId` → `data`), and as platform-api
 * stores it verbatim in `reasoning_results.result_data`. Only the score's own
 * fields are pinned; the rest (pillars, CRAs, diagnoses, the pitch analysis)
 * passes through. platform-api used to check the score by hand and cast the
 * foundations to numbers — a null foundation and the flags saying why were
 * lost on the way to IRR, Passport and every company read.
 */
export const checkupBrainResultSchema = z
  .object({
    /** The headline — the lower bound while `provisional`. */
    iqulture: score0to100,
    /**
     * Read through `resolveIqultureBadge` (the Brain's id when it is one, else
     * the score's badge) — the one badge rule every API reader applies.
     */
    badge: z.object({ id: z.string().optional() }).passthrough().optional(),
    foundations: iqultureFoundationsSchema.extend({ flags: z.array(z.string()) }),
    weights: iqultureWeightsSchema,
    provisional: z.boolean(),
    completeness: iqultureCompletenessSchema,
  })
  .passthrough();
export type CheckupBrainResult = z.infer<typeof checkupBrainResultSchema>;

/**
 * Parse the Brain's CheckUp result, or say why it is not one: the schema's
 * issues, or a completeness that disagrees with its foundations.
 */
export function parseCheckupBrainResult(
  input: unknown
): { ok: true; result: CheckupBrainResult } | { ok: false; reason: string } {
  const parsed = checkupBrainResultSchema.safeParse(input);
  if (!parsed.success) {
    return {
      ok: false,
      reason: parsed.error.issues
        .slice(0, 5)
        .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
        .join('; '),
    };
  }
  const { foundations, iqulture, provisional, completeness } = parsed.data;
  const incoherence = completenessIncoherence({
    score: iqulture,
    foundations,
    provisional,
    completeness,
  });
  return incoherence ? { ok: false, reason: incoherence } : { ok: true, result: parsed.data };
}

export class IQultureContractError extends Error {
  constructor(
    message: string,
    public readonly issues?: unknown
  ) {
    super(message);
    this.name = 'IQultureContractError';
  }
}

/**
 * Why a result's completeness disagrees with itself, or `null` when coherent:
 * a foundation unscored yet covered (or scored yet uncovered), a coverage that
 * is not the weighted sum, a range whose minimum is not the headline, or a
 * provisional mark that does not follow the coverage.
 */
export function completenessIncoherence(result: {
  score: number;
  foundations: Record<IQultureFoundationKey, number | null>;
  provisional: boolean;
  completeness: IQultureCompleteness;
}): string | null {
  const { completeness: c } = result;
  for (const k of IQULTURE_FOUNDATION_KEYS) {
    if ((result.foundations[k] === null) !== (c.foundationCoverage[k] === 0)) {
      return `foundation ${k} is ${result.foundations[k] === null ? 'unscored' : 'scored'} with coverage ${c.foundationCoverage[k]}`;
    }
  }
  const weighted = IQULTURE_FOUNDATION_KEYS.reduce(
    (sum, k) => sum + IQULTURE_FOUNDATION_WEIGHTS[k] * c.foundationCoverage[k],
    0
  );
  if (Math.abs(weighted - c.coverage) > 1e-6) {
    return `coverage ${c.coverage} is not the weighted foundation coverage ${weighted}`;
  }
  if (Math.abs(c.range.min - result.score) > 0.005) {
    return `range.min ${c.range.min} is not the headline score ${result.score}`;
  }
  if (c.range.max < c.range.min) {
    return `range.max ${c.range.max} is below range.min ${c.range.min}`;
  }
  if (result.provisional !== c.coverage < 1) {
    return `provisional=${result.provisional} with coverage ${c.coverage}`;
  }
  return null;
}

/**
 * Validate an assembled iQulture result against the contract AND its
 * coherence rules: the badge follows the score, and the completeness follows
 * the foundations (see {@link completenessIncoherence}). Throws
 * `IQultureContractError` on violation so a malformed AI/stub assembly is
 * caught at the boundary, never persisted.
 */
export function validateIQultureScoreResult(input: unknown): IQultureScoreResult {
  const parsed = iqultureScoreResultSchema.safeParse(input);
  if (!parsed.success) {
    throw new IQultureContractError(
      'iQulture result failed the score-contract schema',
      parsed.error.issues
    );
  }
  const expected = badgeForScore(parsed.data.score);
  if (parsed.data.badge !== expected) {
    throw new IQultureContractError(
      `iQulture badge "${parsed.data.badge}" disagrees with score ${parsed.data.score} (expected "${expected}")`
    );
  }
  const incoherence = completenessIncoherence(parsed.data);
  if (incoherence) {
    throw new IQultureContractError(`iQulture completeness is incoherent: ${incoherence}`);
  }
  return parsed.data;
}
